import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Rating,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { formatDate, parseResponseJSON, formatDateText } from "./CommonFunctions";
import CommonComponents from "./CommonComponents.js";

export const AddRepair = (props) => {
  const [description, setDescription] = useState("");
  const [assignedto, setAssignedto] = useState("");
  const [repairStatus, setRepairStatus] = useState("Yet to be assigned");
  const [scheduledDate, setScheduledDate] = useState("");
  const [completedDate, setCompletedDate] = useState(null);
  const [roomNumber, setRoomNumber] = useState("");
  const [repairId, setRepairId] = useState("");
  const [managerRatingOnRepair, setManagerRatingOnRepair] = useState(0);
  const [managerComments, setManagerComments] = useState("");
  const [allRooms, setAllRooms] = useState([]);
  const [createdAt, setCreatedAt] = useState(new Date());
  const [priority, setPriority] = useState("");
  const [isAddRepair, setIsAddRepair] = useState(false);

  const { displayNotificationMessage } = useContext(CommonComponents);
  const url = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    setIsAddRepair(true);
    getAllRooms();
    if (props.value && props.value.repairId) {
      setRepairId(props.value.repairId);
      getRepairById(props.value.repairId);
    } else if (props.value && props.value.roomNumber) {
      setRoomNumber(props.value.roomNumber);
    }
  }, []);

  const getAllRooms = async () => {
    try {
      const response = await axios.get(`${url}/rooms`);
      setAllRooms(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getRepairById = async (repairId) => {
    try {
      if (repairId) {
        const response = await axios.get(`${url}/repairs/${repairId}`);
        const data = parseResponseJSON(response.data);
        setRoomNumber(data[0].roomid);
        setDescription(data[0].description);
        setAssignedto(data[0].assignedto);
        setScheduledDate(data[0].scheduledDate);
        setCompletedDate(data[0].completedDate);
        setRepairStatus(data[0].status);
        setManagerRatingOnRepair(data[0].managerRatingOnRepair || 0);
        setCreatedAt(data[0].createdAt);
        setPriority(data[0].priority);
      }
    } catch (error) {
      console.log(error);
      displayNotificationMessage("error", error.response.data);
    }
  };

  const handleCloseAddRepair = () => {
    setIsAddRepair(false);
    setRepairId("");
    setManagerRatingOnRepair(0);
    if (props && props.handleCloseAddRepair) {
      props.handleCloseAddRepair();
    }
  };

  const editRepairDetails = async (e, repairId) => {
    if (e) e.preventDefault();
    try {
      await axios.patch(`${url}/repairs/${repairId}`, {
        roomid: roomNumber,
        description,
        status: repairStatus,
        scheduledDate,
        completedDate,
        assignedto,
        managerRatingOnRepair,
        managerComments,
        priority,
      });
      if (props.value && props.value.repairId) {
        props.getRepairs();
      }
      displayNotificationMessage("success", "Updated - Ticket Number: " + repairId);
      handleCloseAddRepair();
    } catch (error) {
      console.log(error);
      displayNotificationMessage("error", error.response.data);
    }
  };

  const saveRepairDetails = async (e) => {
    if (e) e.preventDefault();
    try {
      const response = await axios.post(`${url}/repairs`, {
        roomid: roomNumber,
        description,
        status: repairStatus,
        scheduledDate: scheduledDate || null,
        completedDate: completedDate || null,
        assignedto,
        managerRatingOnRepair: managerRatingOnRepair || null,
        managerComments,
        priority,
      });
      const tempDbResponse = parseResponseJSON(response.data);
      if (tempDbResponse) {
        setRepairId(tempDbResponse.ticketId);
        if (props.value && !props.value.roomNumber) {
          props.getRepairs();
        }
        displayNotificationMessage(
          "success",
          "Created a new Repair Ticket - Ticket Number: " + tempDbResponse.ticketId
        );
        handleCloseAddRepair();
      }
    } catch (error) {
      console.log(error);
      displayNotificationMessage("error", error.response.data);
    }
  };

  return (
    <Dialog
      open={isAddRepair}
      onClose={handleCloseAddRepair}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {repairId ? `Ticket #${repairId}` : "Create New Ticket"}
          </Typography>
          {repairId && (
            <Typography variant="caption" color="text.secondary">
              Created: {formatDateText(formatDate(createdAt))}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleCloseAddRepair} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2.5}>
          {/* Room Number */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Room Number</InputLabel>
              <Select
                value={roomNumber}
                label="Room Number"
                onChange={(e) => setRoomNumber(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select a room
                </MenuItem>
                {allRooms.map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    {room.alias || `Room ${room.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                label="Priority"
                onChange={(e) => setPriority(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select priority
                </MenuItem>
                <MenuItem value="critical">Critical (Urgent)</MenuItem>
                <MenuItem value="high">High (Functional)</MenuItem>
                <MenuItem value="low">Low (Can Wait)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Repair Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
            />
          </Grid>

          {/* Assigned To */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Assigned To"
              value={assignedto}
              onChange={(e) => setAssignedto(e.target.value)}
              size="small"
            />
          </Grid>

          {/* Scheduled Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Scheduled At"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Repair Status</InputLabel>
              <Select
                value={repairStatus}
                label="Repair Status"
                onChange={(e) => setRepairStatus(e.target.value)}
              >
                <MenuItem value="Yet to be assigned">Yet to be Assigned</MenuItem>
                <MenuItem value="Assigned">Assigned</MenuItem>
                <MenuItem value="Need management action">Need Management Action</MenuItem>
                <MenuItem value="In progress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Priority */}
          

          {/* Completed Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Completed At"
              value={completedDate}
              onChange={(e) => setCompletedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          {/* Manager Rating */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="body" color="text.secondary" sx={{ mb: 0 }}>
                Manager Review
              </Typography>
              <Rating
                value={managerRatingOnRepair}
                onChange={(event, newValue) => setManagerRatingOnRepair(newValue)}
                size="large"
              />
            </Box>
          </Grid>

          {/* Manager Comments */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Manager Comments"
              value={managerComments}
              onChange={(e) => setManagerComments(e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleCloseAddRepair} variant="outlined">
          Cancel
        </Button>
        {repairId ? (
          <Button
            onClick={(e) => editRepairDetails(e, repairId)}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Update Changes
          </Button>
        ) : (
          <Button
            onClick={(e) => saveRepairDetails(e)}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Create Ticket
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};